import { type ChangeEvent, useRef, useState } from 'react';
import { FileText, RefreshCw, FileStack } from 'lucide-react';

import { useDocumentUpload } from '../../hooks/useDocumentUpload';
import { useDocuments } from '../../hooks/useDocuments';
import { useDocumentDelete } from '../../hooks/useDocumentDelete';
import { useDocumentReplace } from '../../hooks/useDocumentReplace';
import { useDocumentSync } from '../../hooks/useDocumentSync';
import { type DocumentSummary } from '../../services/api/documents';
import { getApiErrorMessage } from '../../utils/api-error';
import { Button, Alert, Card } from '../../components/ui';
import { DocumentRow } from '../../components/documents/DocumentRow';
import { UploadDocumentForm } from '../../components/documents/UploadDocumentForm';

import { DocSyncStatusPanel } from './DocSyncStatusPanel';
import { useBulkReplace } from './hooks/useBulkReplace';

const ACCEPTED_FILE_TYPES = '.txt,.md,.docx';

export const DocumentsPage = () => {
  const { documents, isLoading, isFetching } = useDocuments();
  const uploadMutation = useDocumentUpload();
  const deleteMutation = useDocumentDelete();
  const replaceMutation = useDocumentReplace();
  const syncMutation = useDocumentSync();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [replacingDocId, setReplacingDocId] = useState<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const bulkReplaceInputRef = useRef<HTMLInputElement | null>(null);

  const clearMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const { runBulkReplace, isBulkReplacing } = useBulkReplace({
    onResult: ({ message, isError }) =>
      isError ? setErrorMessage(message) : setSuccessMessage(message),
  });

  const handleBulkReplaceFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (event.target) {
      event.target.value = '';
    }
    clearMessages();
    await runBulkReplace(files, documents);
  };

  const handleReplaceClick = (docId: string) => {
    setReplacingDocId(docId);
    clearMessages();
    replaceInputRef.current?.click();
  };

  const handleReplaceFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const docId = replacingDocId;
    if (event.target) {
      event.target.value = '';
    }
    if (!file || !docId) {
      setReplacingDocId(null);
      return;
    }

    replaceMutation.mutate(
      { id: docId, file },
      {
        onSuccess: () => {
          setSuccessMessage('Dokumentets innehåll ersattes.');
          setReplacingDocId(null);
        },
        onError: (error) => {
          setErrorMessage(getApiErrorMessage(error, 'Kunde inte ersätta dokumentets innehåll.'));
          setReplacingDocId(null);
        },
      },
    );
  };

  const handleDelete = (document: DocumentSummary) => {
    const confirmed = window.confirm(
      `Är du säker på att du vill ta bort dokumentet "${document.title}"?`,
    );
    if (!confirmed) return;

    clearMessages();
    deleteMutation.mutate(document.id, {
      onSuccess: () => setSuccessMessage('Dokumentet togs bort.'),
      onError: (error) =>
        setErrorMessage(getApiErrorMessage(error, 'Kunde inte ta bort dokumentet.')),
    });
  };

  const hasDocuments = documents.length > 0;
  const isLoadingDocuments = isLoading || (isFetching && !hasDocuments);

  return (
    <section className="animate-float-in space-y-8">
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleReplaceFileChange}
        className="hidden"
      />
      <input
        ref={bulkReplaceInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        multiple
        onChange={handleBulkReplaceFileChange}
        className="hidden"
      />
      <header className="mb-8">
        <div className="relative">
          <h2 className="bg-gradient-to-r from-brand-blue to-brand-blue/80 bg-clip-text text-4xl font-bold text-transparent">
            Dokument & Kunskapsbas
          </h2>
          <div className="absolute -inset-1 bg-gradient-to-r from-brand-blue/20 to-transparent opacity-30 blur-xl" />
        </div>
        <p className="text-gray mt-3">
          Hantera dokument och träning av agentens kontextuella kunskap.
        </p>
        {isFetching && !isLoading ? (
          <p className="text-xs text-slate-500">Uppdaterar dokumentlistan…</p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={syncMutation.isPending}
            onClick={() => {
              clearMessages();
              syncMutation.mutate(undefined, {
                onSuccess: (result) => {
                  const summary = `Docs synk klar: ${result.synced} synkade, ${result.skipped} skippade, ${result.errors} fel.`;
                  if (result.errors > 0) {
                    setErrorMessage(summary);
                    return;
                  }
                  setSuccessMessage(summary);
                },
                onError: (error) => {
                  setErrorMessage(getApiErrorMessage(error, 'Kunde inte synka dokument.'));
                },
              });
            }}
          >
            {syncMutation.isPending ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Synkar docs...
              </>
            ) : (
              <>
                <RefreshCw size={16} className="mr-2" />
                Synka docs nu
              </>
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={isBulkReplacing}
            onClick={() => {
              clearMessages();
              bulkReplaceInputRef.current?.click();
            }}
          >
            {isBulkReplacing ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Ersätter alla...
              </>
            ) : (
              <>
                <FileStack size={16} className="mr-2" />
                Ersätt alla (batch)
              </>
            )}
          </Button>
        </div>
      </header>

      <div className="space-y-8">
        <DocSyncStatusPanel />

        <Card className="overflow-hidden">
          <div className="mb-6 flex items-center gap-3">
            <div className="ml-2 mt-2 rounded-lg bg-brand-blue/10 p-2 text-brand-blue">
              <FileText size={24} />
            </div>
            <h3 className="text-xl font-semibold text-brand-blue">Dokument</h3>
          </div>

          {isLoadingDocuments ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-brand-blue border-t-transparent"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-brand-blue/70">Laddar dokument…</span>
            </div>
          ) : hasDocuments ? (
            <div className="space-y-4 p-1">
              {documents.map((document, index) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  animationDelayMs={index * 100}
                  isReplacing={replaceMutation.isPending && replacingDocId === document.id}
                  isDeleting={deleteMutation.isPending}
                  onReplace={handleReplaceClick}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue/10 p-4 text-brand-blue">
                <FileText size={32} />
              </div>
              <p className="text-brand-blue/60">Inga dokument uppladdade ännu.</p>
              <p className="mt-2 text-sm text-brand-blue/40">
                Ladda upp ditt första dokument nedan.
              </p>
            </div>
          )}
        </Card>

        <UploadDocumentForm
          isUploading={uploadMutation.isPending}
          onFileSelected={clearMessages}
          onUpload={(file, description, reset) => {
            clearMessages();
            uploadMutation.mutate(
              { file, description },
              {
                onSuccess: () => {
                  setSuccessMessage('Dokumentet laddades upp.');
                  reset();
                },
                onError: (error) => {
                  setErrorMessage(getApiErrorMessage(error, 'Kunde inte ladda upp dokumentet.'));
                },
              },
            );
          }}
        >
          {errorMessage && <Alert type="error" message={errorMessage} className="mb-6" />}
          {successMessage && <Alert type="success" message={successMessage} className="mb-6" />}
        </UploadDocumentForm>
      </div>
    </section>
  );
};
